package ui.campaign

import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.graphics.*
import androidx.compose.ui.graphics.vector.*
import androidx.compose.ui.text.style.*
import androidx.compose.ui.unit.*
import api.*
import common.frontend.api.*
import compose.assets.*
import compose.assets.svg.*
import org.jetbrains.compose.resources.*
import provider.*
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.*
import tk.mallumo.compose.navigation.viewmodel.*
import tools.*
import ui.*
import ui.widget.*
import kotlin.math.*
import kotlin.time.Duration.Companion.hours


interface CampaignListingScope {

    val vmGlobal: GlobalVM
    val vm: CampaignListingVM
    val nav: Navigation
    val args: CampaignListingArgs
    val login: Io.User
    val action: (CampaignListingVM.Action) -> Unit
    val display: CommonDisplayProvider

    companion object {

        private val PROGRESS_FUN: @Composable (Wrapper.Item) -> Any = {
            ListingItemBody.Progress(it.counter.progress.coerceIn(0F, 1F))
        }

        private val FULFILLMENT_FUN: @Composable (Wrapper.Item) -> Any = { item ->
            if (item.data.isCommercial) {
                ListingItemBody.Progress(item.counter.pctCurrent)
            } else {
                item.counter.counter.all
            }
        }

        private val STATE_LABEL_FUN: @Composable (Wrapper.Item) -> String = {
            CampaignState[it.data.state].labelRes().rememberString()
        }

        private val STATE_ICON_FUN: @Composable (Wrapper.Item) -> ListingItemBody.Icon = {
            ListingItemBody.Icon(
                icon = CampaignState[it.data.state].icon(),
                tint = when {
                    CampaignState.DRAFT match it.data.state -> MaterialTheme.colorScheme.primary
                    else -> Color.White
                }
            )
        }

        val coreHeaderSpecs = listOf(
            ListingSpecs.Item(title = Res.string.layer_top_listing__item_id, width = 50.dp, align = TextAlign.Center) { ListingSpecs.trimmedId(it.data.id) },
            ListingSpecs.Item(title = Res.string.layer_top_listing__item_state, width = 90.dp, body = STATE_LABEL_FUN),
            ListingSpecs.Item(title = Res.string.layer_top_listing__item_progress, width = 80.dp, body = PROGRESS_FUN),
            ListingSpecs.Item(title = Res.string.layer_top_listing__item_fulfillment, width = 80.dp, align = TextAlign.End, body = FULFILLMENT_FUN),
            ListingSpecs.Item(title = Res.string.layer_top_listing__item_name, width = 300.dp, lines = 2) { it.data.name },
            ListingSpecs.Item(title = Res.string.layer_top_listing__item_start, width = 80.dp) { it.data.start.date.formatFromRes() },
            ListingSpecs.Item(title = Res.string.layer_top_listing__item_end, width = 80.dp) { it.data.end.date.formatFromRes() },
            ListingSpecs.Item(title = Res.string.layer_top_listing__item_visuals, width = 60.dp) { it.data.visuals.size },
            ListingSpecs.Item(title = Res.string.layer_top_listing__item_devices, width = 80.dp) { it.data.devices },
            ListingSpecs.Item(title = Res.string.layer_top_listing__item_impressions, width = 110.dp) { it.data.impressionsSum },
        )
        val headerSpecs = ListingSpecs.create(
            *buildList {
                add(ListingSpecs.Item(title = Res.string.global_empty, width = 18.dp, align = TextAlign.Center, body = STATE_ICON_FUN))
                addAll(coreHeaderSpecs)
            }.toTypedArray()
        )

        @Composable
        fun rememberScope(): CampaignListingScope {
            val nav = LocalNavigation.current
            val account = LocalAccountProvider.current
            val args = LocalNavigationArgs.current
            val arg = args.rememberArgs(CampaignListingArgs::class)
            val vm = nav.viewModel(CampaignListingVM::class, arg.companyId.toString())
            val vmGlobal = globalViewModel(GlobalVM::class)
            val action = CampaignListingVM.Action.rememberAction(vm)
            val display = LocalDisplayProvider.current
            return remember {
                object : CampaignListingScope {
                    override val vmGlobal: GlobalVM
                        get() = vmGlobal
                    override val vm: CampaignListingVM
                        get() = vm
                    override val nav: Navigation
                        get() = nav
                    override val args: CampaignListingArgs
                        get() = arg
                    override val login: Io.User
                        get() = account.current!!
                    override val action: (CampaignListingVM.Action) -> Unit
                        get() = action
                    override val display: CommonDisplayProvider
                        get() = display


                }.also {
                    vm.init(it)
                }
            }
        }
    }

    class Wrapper(val data: Io.ListingCampaign) : EFieldList<Io.ListingCampaign.Item, Wrapper.Item>(
        items = data.items,
        onBindEntry = { Item(it) }
    ) {
        class Item(
            override val data: Io.ListingCampaign.Item,
            override val key: String = data.id.toString(),
        ) : Entry<Io.ListingCampaign.Item> {

            private val hourInMs = 1.hours.inWholeMilliseconds.toFloat()
            private var _impressionSpace = calculateSpace()

            val counter = ImpressionCalculator(
                containsCommercial = data.isCommercial,
                containsNonCommercial = data.isNonCommercial,
                real = data.impressionsSum.toLong(),
                targetMax = data.impressionExpected,
                start = data.start.date,
                end = data.end.date
            )

            val name: EFieldTextString = EFieldTextString(
                property = data::name,
                condition = { it.trim().takeIf { it.isNotEmpty() } },
                onChange = { makeChange() },
                onErr = { makeChange() })

            val isCommercial: EFieldRegularBoolean = EFieldRegularBoolean(
                property = data::isCommercial,
                onChange = { makeChange() })


            val slotsCount: EFieldRegularInt = EFieldRegularInt(
                property = data.campaignSetting::itemCount,
                onChange = { makeChange() })

            val impressionCount: EFieldTextInt = EFieldTextInt(
                property = data.campaignSetting::itemCount,
                condition = { it.trim().toIntOrNull()?.takeIf { it > 0 } },
                onChange = {
                    makeChange()
                    impressionSpace.set(calculateSpace().toString().toTextFieldValue())
                })

            val impressionDuration: EFieldTextMsAsSec = EFieldTextMsAsSec(
                property = data.campaignSetting::itemDuration,
                condition = { it.trim().toLongOrNull()?.takeIf { it > 0 } },
                onChange = {
                    makeChange()
                    impressionSpace.set(calculateSpace().toString().toTextFieldValue())
                })


            val impressionSpace: EFieldTextFloat = EFieldTextFloat(
                property = ::_impressionSpace,
                condition = { it.trim().toFloatOrNull()?.takeIf { it in 0F..100F } },
                onChange = { newSpace ->
                    makeChange()
                    calculateImpression(newSpace)
                        ?.takeIf { it != impressionCount.state }
                        ?.toString()
                        ?.toTextFieldValue()
                        ?.also { impressionCount.set(it) }
                })

            private fun calculateImpression(space: Float): Int? {
                return kotlin.runCatching {
                    (((space / 100F) * hourInMs) / data.campaignSetting.itemDuration.toFloat()).roundToInt()
                }.getOrNull()
            }

            private fun calculateSpace(): Float {
                return runCatching {
                    @Suppress("ComplexRedundantLet", "SimpleRedundantLet")
                    ((data.campaignSetting.itemDuration * data.campaignSetting.itemCount).toFloat())
                        .let { it / hourInMs }
                        .let { it * 10000F }
                        .let { it.roundToInt() }
                        .let { it.toFloat() / 100F }
                }.getOrNull() ?: 0F
            }

            var change by mutableStateOf(false)
                private set

            private fun makeChange() {
                change = when {
                    name.err -> false
                    else -> when (CampaignType[data.campaignSetting.type]) {
                        CampaignType.NONE -> true
                        CampaignType.SLOT -> true
                        CampaignType.IMPRESSION -> when {
                            impressionCount.err -> false
                            impressionDuration.err -> false
                            impressionSpace.err -> false
                            else -> true
                        }
                    }
                }
            }
        }
    }
}

fun CampaignState.labelRes(): StringResource {
    return when (this) {
        CampaignState.DRAFT -> Res.string.layer_top_listing__item_state_incomplete
        CampaignState.STOPPED -> Res.string.layer_top_listing__item_state_stopped
        CampaignState.DELETED -> Res.string.layer_top_listing__item_state_deleted
        CampaignState.ACTIVE -> Res.string.layer_top_listing__item_state_active
        CampaignState.FINISHED -> Res.string.layer_top_listing__item_state_finished
        CampaignState.PUBLISHED -> Res.string.layer_top_listing__item_state_published
        CampaignState.PAUSED -> Res.string.layer_top_listing__item_state_paused
    }
}

fun CampaignState.icon(): ImageVector {
    return when (this) {
        CampaignState.DRAFT -> Svg.Edit
        CampaignState.STOPPED -> Svg.Hand_back_left
        CampaignState.DELETED -> Svg.DeleteEmpty
        CampaignState.ACTIVE -> Svg.Triangle
        CampaignState.FINISHED -> Svg.Check_all
        CampaignState.PUBLISHED -> Svg.Sleep
        CampaignState.PAUSED -> Svg.Pause
    }
}
